import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type UseSearchParams = () => [params: URLSearchParams, search: string];

export const useGetSearchParams: UseSearchParams = () => {
    const { search } = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);

    return [params, search];
};

type UseSearchParamReturn<T extends string = string> = [paramValue: T | null, search: string];

export const useGetSearchParam = <T extends string = string>(
    param: string,
): UseSearchParamReturn<T> => {
    const [params, search] = useGetSearchParams();

    const paramValue = (params.get(param) ?? null) as T | null;

    return [paramValue, search];
};

const useCreateSearchParam = (param: string) => {
    const location = useLocation();

    return useCallback(
        (paramValue?: string | null) => {
            const searchParams = new URLSearchParams(location.search);
            if (paramValue) {
                searchParams.set(param, paramValue);
            } else {
                searchParams.delete(param);
            }
            return searchParams;
        },
        [location.search, param],
    );
};

type UseSearchParamPath = (
    param: string,
    paramValue?: string | null,
) => [path: string, setSearchParam: (replace?: boolean) => URLSearchParams];

export const useSetSearchParamPath: UseSearchParamPath = (param, paramValue) => {
    const history = useHistory();
    const location = useLocation();
    const createSearchParam = useCreateSearchParam(param);

    const searchParams = useMemo(
        () => createSearchParam(paramValue),
        [createSearchParam, paramValue],
    );

    const path = useMemo(() => {
        if (searchParams.size === 0) {
            return location.pathname;
        }
        return `${location.pathname}?${searchParams.toString()}`;
    }, [location.pathname, searchParams]);

    const setSearchParam = useCallback(
        (replace?: boolean) => {
            if (replace) {
                history.replace({
                    pathname: location.pathname,
                    search: searchParams.toString(),
                });
            } else {
                history.push({
                    pathname: location.pathname,
                    search: searchParams.toString(),
                });
            }
            return searchParams;
        },
        [searchParams, history, location.pathname],
    );

    return [path, setSearchParam];
};

export type UseSetSearchParamReturn<T extends string = string> = [
    paramValue: T | null,
    setSearchParam: (paramValue?: string | null, replace?: boolean) => URLSearchParams,
];

export const useSetSearchParam = <T extends string = string>(
    param: string,
): UseSetSearchParamReturn<T> => {
    const history = useHistory();
    const location = useLocation();
    const createSearchParam = useCreateSearchParam(param);

    const [paramValue] = useGetSearchParam<T>(param);

    const setSearchParam = useCallback(
        (paramValue?: string | null, replace?: boolean) => {
            const searchParams = createSearchParam(paramValue);
            if (replace) {
                history.replace({
                    pathname: location.pathname,
                    search: searchParams.toString(),
                });
            } else {
                history.push({
                    pathname: location.pathname,
                    search: searchParams.toString(),
                });
            }
            return searchParams;
        },
        [createSearchParam, history, location.pathname],
    );

    return [paramValue, setSearchParam];
};
